/*
 * ПРОГРЕСС УЧЕНИКА ПО УРОКАМ.
 *
 * Хранится в localStorage браузера. Это осознанный выбор для первой версии:
 * прогресс — вещь личная и небольшая, ради него не стоит заводить таблицу
 * и ручки в бэкенде, пока механику уроков ещё обкатывают на живом ребёнке.
 * Когда формат уроков устоится, тот же интерфейс (loadProgress/saveProgress)
 * можно будет перевести на облако, не трогая ни панель, ни проверку.
 *
 * Ключ включает id пользователя: за одним компьютером занимаются двое детей,
 * и прогресс старшего не должен подменять прогресс младшего.
 */

const STORAGE_PREFIX = 'kubikon-cad-lessons:';

/** Ключ хранилища для текущего ученика. */
function storageKey(userId) {
    return STORAGE_PREFIX + (userId || 'guest');
}

/**
 * Прочитать весь прогресс ученика.
 * @returns {Object} карта { [lessonId]: { done, steps:[], attempts, updatedAt } }
 */
export function loadProgress(userId) {
    try {
        const raw = localStorage.getItem(storageKey(userId));
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch (_) {
        // Битые данные не должны ронять редактор — начинаем с чистого листа.
        return {};
    }
}

/** Записать весь прогресс ученика. */
function saveProgress(userId, progress) {
    try {
        localStorage.setItem(storageKey(userId), JSON.stringify(progress));
    } catch (_) { /* заполнено или запрещено — молча продолжаем */ }
}

/** Прогресс по одному уроку (с безопасными значениями по умолчанию). */
export function lessonProgress(userId, lessonId) {
    const all = loadProgress(userId);
    const p = all[lessonId] || {};
    return {
        done: !!p.done,
        steps: Array.isArray(p.steps) ? p.steps : [],
        attempts: Number(p.attempts) || 0,
        updatedAt: p.updatedAt || null,
    };
}

/** Изменить прогресс урока: patch накладывается поверх существующего. */
export function updateLessonProgress(userId, lessonId, patch) {
    const all = loadProgress(userId);
    const prev = all[lessonId] || {};
    all[lessonId] = {
        ...prev,
        ...patch,
        updatedAt: new Date().toISOString(),
    };
    saveProgress(userId, all);
    return all[lessonId];
}

/** Отметить или снять галочку у шага практики. */
export function toggleStep(userId, lessonId, stepIndex) {
    const p = lessonProgress(userId, lessonId);
    const steps = new Set(p.steps);
    if (steps.has(stepIndex)) steps.delete(stepIndex);
    else steps.add(stepIndex);
    return updateLessonProgress(userId, lessonId, {
        steps: [...steps].sort((a, b) => a - b),
    });
}

/** Засчитать попытку проверки. Урок пройден — только при успехе. */
export function recordAttempt(userId, lessonId, passed) {
    const p = lessonProgress(userId, lessonId);
    return updateLessonProgress(userId, lessonId, {
        attempts: p.attempts + 1,
        // Пройденный урок не «разучивается» неудачной попыткой позже.
        done: p.done || passed,
    });
}

/** Сколько уроков курса пройдено — для списка курсов. */
export function courseProgress(userId, course) {
    const all = loadProgress(userId);
    const done = course.lessons.filter((l) => all[l.id]?.done).length;
    return { done, total: course.lessons.length };
}
